import { PlusIcon, TrashIcon, GripVerticalIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TAG_TYPE_CONFIGS, type TagType, type NIP51ListKind } from '@/lib/nip51';

export interface ListFormItem {
  id: string;
  tag: TagType;
  value: string;
  label: string;
}

interface ListItemEditorProps {
  kindConfig: NIP51ListKind;
  items: ListFormItem[];
  onChange: (items: ListFormItem[]) => void;
}

export function ListItemEditor({ kindConfig, items, onChange }: ListItemEditorProps) {
  const addItem = (tag: TagType) => {
    onChange([...items, { id: String(Date.now()), tag, value: '', label: '' }]);
  };

  const removeItem = (id: string) => {
    onChange(items.filter((i) => i.id !== id));
  };

  const updateItem = (id: string, field: 'value' | 'label', val: string) => {
    onChange(items.map((i) => (i.id === id ? { ...i, [field]: val } : i)));
  };

  // Group items by tag type for display
  const tagTypes = kindConfig.tagTypes;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <span>{kindConfig.icon} List Items</span>
          {tagTypes.length > 1 && (
            <span className="text-xs font-normal text-muted-foreground">
              — supports {tagTypes.map((t) => TAG_TYPE_CONFIGS[t].label).join(', ')}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            No items yet. Add your first item below.
          </p>
        )}

        {items.map((item) => {
          const tagCfg = TAG_TYPE_CONFIGS[item.tag];
          return (
            <div key={item.id} className="flex items-start gap-2">
              <GripVerticalIcon className="size-4 text-muted-foreground mt-2.5 shrink-0" />

              <div className="flex-1 min-w-0 space-y-1.5">
                {tagTypes.length > 1 && (
                  <Badge variant="secondary" className="text-xs capitalize">
                    {tagCfg.label}
                  </Badge>
                )}
                <div className={`grid gap-2 ${tagCfg.hasLabel ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                  <Input
                    placeholder={tagCfg.placeholder}
                    value={item.value}
                    onChange={(e) => updateItem(item.id, 'value', e.target.value)}
                  />
                  {tagCfg.hasLabel && (
                    <Input
                      placeholder="Label (optional)"
                      value={item.label}
                      onChange={(e) => updateItem(item.id, 'label', e.target.value)}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{tagCfg.hint}</p>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="size-8 p-0 shrink-0 mt-0.5"
                onClick={() => removeItem(item.id)}
              >
                <TrashIcon className="size-4" />
              </Button>
            </div>
          );
        })}

        {/* Add buttons — one per supported tag type */}
        <div className="flex flex-wrap gap-2 pt-1">
          {tagTypes.map((tag) => (
            <Button
              key={tag}
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => addItem(tag)}
            >
              <PlusIcon className="size-3.5" />
              Add {TAG_TYPE_CONFIGS[tag].label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
